import { ISectionLogger } from "@multiweb/logging";
import { CancellationToken, RequestContext } from "@multiweb/types/multiweb";
import { Triplet } from "Global/Base/DataTypes/NTuples";
import { LogicalOperations, LogicalOperator } from "Global/Base/LogicalOperations";
import { ReferenceParameter } from "Global/Base/ReferenceParameter";
import { IItemRights, RightType } from "Global/Base/Rights/RightTypes";
import { ArrayUtils } from "Global/Base/Utils/ArrayUtils";
import { EnumUtils } from "Global/Base/Utils/EnumUtils";
import { IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { NullOWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "OW.Remoting/Remoting/OWManager";
import { IMultiEditObject } from "UI/UI.Controls/DataSource/MultiEdit/MEOWObject";
import { FrameSharedMethods } from "UI/UI/UI/XGControls/Frames/FrameSharedMethods";
import { IDetailFrame } from "UI/UI/UI/XGControls/Frames/IDetailMasterableFrame";
import { IForm } from "UI/UI/UI/XGControls/IForm";
import { IFrameWithRights, IListFrame } from "UI/UI/UI/XGControls/IFrame";
import { convertContextFromFrameToRequest } from "UI/UI/Utilities/SharedUIMethods";
import { ClassItemRightTypes, IClassItem, IClassItemRights, IEvaluatedConditionResult } from "../../../../OW.Remoting/Remoting/Rights";
import { IFormRight } from "../../../../UI/UI/Rights/FormRight";
import { ROLELESS_ROLE_ID } from "../constants";
import { IRightsContext } from "../IRightsContext";
import { StandardOperationRights } from "../StandardOperationRights";
import { RightsParams } from "./RightsParams";
import { IRightsEvaluator, IRightsParams } from "./types";

/** Abstraktní vyhodnocovač práv.
 * Obsahuje společné metody pro single a hromadnou editaci
 * */
export abstract class BaseRightsEvaluator implements IRightsEvaluator {
    protected readonly _context: IRightsContext | null;
    protected readonly _logger: ISectionLogger;
    /**
     * Z dat práva a vyhodnocených podmínek vypočítá výslednou hodnotu práva
     * @param itemRight Data práva
     * @param evaluatedConditions Vyhodnocené podmínky
     */
    protected abstract _getRightValue(itemRight: IItemRights, evaluatedConditions: IEvaluatedConditionResult[], rightType: RightType): IItemRights;

    public abstract evaluateCondition(conditionID: string, isStatic: boolean, rightType: RightType, requestContext?: RequestContext): Promise<IEvaluatedConditionResult>;

    constructor(logger: ISectionLogger, context: IRightsContext | null) {
        this._logger = logger;
        this._context = context;
    }

    private async _getIEDRightParams(frame: IFrameWithRights, usesFormRights: boolean, refCancellationToken?: ReferenceParameter<CancellationToken>): Promise<IRightsParams> {
        let masterInstanceParams: { instanceId: string | null; upgradedCt?: CancellationToken } | undefined = undefined;

        const cancellationToken = refCancellationToken && refCancellationToken.value;

        let masterObject: IOWObject | null;
        let masterLinkToAttr: string | null;

        if (frame) {
            if (frame.actualObject == null && !frame.masterFrame && frame.ownerForm?.masterLinkToAttr) {
                masterObject = frame.ownerForm?.masterObject;
                if (masterObject) {
                    masterLinkToAttr = frame.ownerForm.masterLinkToAttr;
                }
            } else if (frame?.masterLinkToAttr) {
                masterObject = frame?.masterObject;
                masterLinkToAttr = frame.masterLinkToAttr;
                masterInstanceParams = await FrameSharedMethods.getMasterAttrInstanceId(masterObject, frame.masterAttr, this._getRequestContext(frame, cancellationToken));
                if (refCancellationToken)
                    refCancellationToken.value = masterInstanceParams!.upgradedCt;
            }
        }

        return new RightsParams(usesFormRights, frame?.actualObject?.objectName ?? frame?.objectName, masterObject, masterLinkToAttr);
    }

    /** Vyhodnotí IED práva bez objektu */
    private async _getIEDRightsWithoutObject(ownerForm: IForm, frame: IFrameWithRights, currentIEDRights: StandardOperationRights, rightsParams: IRightsParams, requestContext?: RequestContext): Promise<StandardOperationRights> {
        if (currentIEDRights.strictlyDisabled)
            return currentIEDRights;
        let iedResult = currentIEDRights;
        let canInsert: boolean;

        const getInsertRight = async (rightsParams: IRightsParams): Promise<boolean> => {
            const insertRight = await OWManager.getInsertRightWithValidationRights(rightsParams.objectName, rightsParams.masterObjectInstanceID, rightsParams.masterLinkToAttr, requestContext);

            if (rightsParams.evaluateFormRights && ownerForm.canInsertAtFormLevel === false) {
                // Pokud se na formuláři vyhodnotilo false a v souběhu existuje formulářové právo pro každou roli, která má právo na rozhraní, tak je výsledek false, jinak se bere hodnota ze serveru.
                return insertRight.InsertRoleIDs.every(roleId => ownerForm.formRights.find(formRight => formRight.roleID === roleId)) ? false : insertRight.InsertRightValue;
            } else {
                return insertRight.InsertRightValue;
            }
        };

        if (!(frame instanceof IListFrame) || frame.hasInsertControl) {
        // Celá metoda je pro případy bez actualObjectu
            if (!frame.masterFrame) {
            // frame bez mastera
                if (!frame.isInitialized || frame.canInsert == null) {
                    canInsert = await getInsertRight(rightsParams);
                    frame.canInsert = canInsert;
                } else {
                    canInsert = frame.canInsert;
                }
            } else {
            // frame s masterem
                canInsert = await getInsertRight(rightsParams);
                if (!frame.isInitialized || frame.canInsert == null)
                    frame.canInsert = canInsert;
            }
        } else {
            canInsert = false;
            if (!frame.isInitialized || frame.canInsert == null)
                frame.canInsert = canInsert;
        }

        if (!rightsParams.evaluateFormRights) {
            iedResult = new StandardOperationRights(requestContext.roles, true, true, true);
        }
        iedResult.canNew = canInsert;

        return iedResult;
    }

    /** Vyhodnotí práva formulářové úrovně pro zadaný context */
    protected async _evaluateClassItemRight(classItem: IClassItem, classItemRightType: ClassItemRightTypes, requestContext?: RequestContext, evaluateWithoutAO?: boolean): Promise<IClassItemRights | null> {
        if ((this._context && !(this._context instanceof NullOWObject)) || evaluateWithoutAO) {
            return (this._context && !evaluateWithoutAO) ? // Na objektu chceme vyhodnotit pokud máme objekt, nebo pokud je nastaven příznak na vyhodnocení bez něj (pro statické metody)
                this._context.itemRights(classItem.name, classItem.type, classItemRightType, requestContext?.cancellationToken) :
                OWManager.staticMethodsRights(classItem.name, requestContext);
        }
        return null;
    }

    protected _getRequestContext(frame: IFrameWithRights, cancellationToken: CancellationToken = CancellationToken.none): RequestContext {
        return convertContextFromFrameToRequest(frame?.frameContext, cancellationToken);
    }

    protected async _getMainFrameInsertRight(frame: IFrameWithRights, cancellationToken: CancellationToken): Promise<boolean | null> {
        if (frame.ownerForm.canInsertAtFormLevel != null && !frame.ownerForm.canInsertAtFormLevel)
            return false;

        // pokud není vkládání zakázáno už na úrovni formulářů, tak se ještě vyhodnotí serverová práva
        if (frame instanceof IDetailFrame && frame.masterFrame && !frame.ignoreMasterRightsObject) {
            // právo vkládání u detailu vyhodnocujeme na jeho masterFramu (LF, ...)
            frame = frame.masterFrame;
        }

        if (frame.objectName && !(frame.actualObject instanceof IMultiEditObject)) {
            const masterLinkParams = await FrameSharedMethods.getMasterAttrInstanceId(frame.masterObject, frame.masterAttr, this._getRequestContext(frame, cancellationToken));
            const masterInstanceID: string = masterLinkParams.instanceId;
            const masterLinkToAttr: string = frame.masterLinkToAttr;
            if (masterLinkParams.upgradedCt)
                cancellationToken = masterLinkParams.upgradedCt;
            return (await OWManager.getInsertRightWithValidationRights(frame.objectName, masterInstanceID, masterLinkToAttr, this._getRequestContext(frame, cancellationToken))).InsertRightValue;
        }

        return null;
    }
    /** Vrací validní práva pro 6) formulář implmentační bez role a 7) Formulář s rolí  */
    protected _getValidItemRights(formActiveRoles: IFormRight[], formRightWithoutRole: IFormRight, evaluatedFormEDConditions: IEvaluatedConditionResult[], rightType: RightType): IItemRights[] {
        const validItemRights: IItemRights[] = [];
        if (formRightWithoutRole) {
            validItemRights.push(this._getRightValue(this._getItemRights(formRightWithoutRole, rightType), evaluatedFormEDConditions, rightType)); // 6)
        }
        for (const formRight of formActiveRoles) {// pouze aktivní role
            validItemRights.push(this._getRightValue(this._getItemRights(formRight, rightType), evaluatedFormEDConditions, rightType)); // 7)
        }
        return validItemRights;
    }

    /** Vrací definici práv pro zadané formulářové právo */
    protected _getItemRights(item: IFormRight, rightType: RightType): IItemRights {
        switch (rightType) {
            case RightType.edit:
                return item.getEditRight();
            case RightType.insert:
                return item.getInsertRight();
            case RightType.read:
                return item.getReadRight();
            case RightType.delete:
                return item.getDeleteRight();
        }
        throw new Error();
    }

    /**
     * Vyhodnotí právo vkládání pro aktuální formulář
     */
    protected async _evaluateInsertRight(iedResult: StandardOperationRights, frame: IFrameWithRights, rightsParams: IRightsParams, cancellationToken: CancellationToken): Promise<void> {
        if (!rightsParams.evaluateFormRights) {
            iedResult.canNew = (await OWManager.getInsertRightWithValidationRights(rightsParams.objectName, rightsParams.masterObjectInstanceID, rightsParams.masterLinkToAttr, this._getRequestContext(frame, cancellationToken)))
                .InsertRightValue;
        } else {
            if (frame.canInsert != null) {
                iedResult.canNew = frame.canInsert;
            } else {
                const frameInsertRight = await this._getMainFrameInsertRight(frame, cancellationToken);
                if (frameInsertRight != null) {
                    frame.canInsert = frameInsertRight;
                    iedResult.canNew = frame.canInsert;
                }
            }
        }
    }

    public async evaluateServerRight(classItem: IClassItem, rightToEvaluate?: RightType, requestContext?: RequestContext, evaluateWithoutAO?: boolean): Promise<IClassItemRights | null> {
        let classItemRightTypes: ClassItemRightTypes | undefined = undefined;
        switch (rightToEvaluate) {
            case undefined:
                classItemRightTypes = ClassItemRightTypes.all;
                break;
            case RightType.visible:
                classItemRightTypes = ClassItemRightTypes.visible;
                break;
            case RightType.enabled:
                classItemRightTypes = ClassItemRightTypes.enabled;
                break;
            case RightType.required:
                classItemRightTypes = ClassItemRightTypes.required;
                break;
            case RightType.postponedRequired:
                classItemRightTypes = ClassItemRightTypes.postponedRequired;
                break;
            case RightType.recommended:
                classItemRightTypes = ClassItemRightTypes.recommended;
                break;
        }

        if (classItemRightTypes != null)
            return await this._evaluateClassItemRight(classItem, classItemRightTypes, requestContext, evaluateWithoutAO);

        return null;
    }

    public async evaluateConditions(conditionIDs: string[], areStatic: boolean, rightType: RightType, requestContext?: RequestContext): Promise<IEvaluatedConditionResult[]> {
        if (conditionIDs && conditionIDs.length > 0) {
            return Promise.all(conditionIDs.map(c => this.evaluateCondition(c, areStatic, rightType, requestContext)));
        }

        return null;
    }

    /**
    * Vyhodnotí právo editace pro aktuální kontext
    */
    protected _evaluateEditRight(iedResult: StandardOperationRights, evaluateFormRights: boolean, formActiveRoles: IFormRight[], formRightWithoutRole: IFormRight, evaluatedFormEDConditions: IEvaluatedConditionResult[]): void {
        let validItemRights: IItemRights[] = [];

        if (evaluateFormRights) {
            validItemRights = this._getValidItemRights(formActiveRoles, formRightWithoutRole, evaluatedFormEDConditions, RightType.edit);
            if (validItemRights.length <= 0) {
                iedResult.canEdit = this._context.canEdit;
            }
        } else {
            iedResult.canEdit = this._context.canEdit;
        }

        iedResult.contextEditRoleIds = this._context.editRoleIds;
        iedResult.setEditRightsResults(validItemRights);
    }

    /**
    * Vyhodnotí právo mazání pro aktuální kontext
    */
    protected _evaluateDeleteRight(iedResult: StandardOperationRights, evaluateFormRights: boolean, formActiveRoles: IFormRight[], formRightWithoutRole: IFormRight, evaluatedFormEDConditions: IEvaluatedConditionResult[]): void {
        let validItemRights: IItemRights[] = [];

        if (evaluateFormRights) {
            validItemRights = this._getValidItemRights(formActiveRoles, formRightWithoutRole, evaluatedFormEDConditions, RightType.delete);

            if (validItemRights.length <= 0) {
                iedResult.canDelete = this._context.canDelete;
            }
        } else {
            iedResult.canDelete = this._context.canDelete;
        }

        iedResult.contextDeleteRoleIds = this._context.deleteRoleIds;
        iedResult.setDeleteRightsResults(validItemRights);
    }

    public async evaluateIEDRights(rightType: RightType, ownerForm: IForm, frame: IFrameWithRights, evaluateFormRights: boolean, cancellationToken?: CancellationToken): Promise<StandardOperationRights> {
        //TODO: ownerform vyextrahovat jen iface pro formpráva
        const requestContext = this._getRequestContext(frame, cancellationToken);
        const activeRoleIds = requestContext.roles || await OWManager.getUserRoleIDs(requestContext?.cancellationToken);
        const iedResult = FrameSharedMethods.getDefaultIEDRights(ownerForm, activeRoleIds);

        if (this._context == null) {
            if (!EnumUtils.hasFlag(rightType, RightType.insert))
                return iedResult;
            const refCt = new ReferenceParameter<CancellationToken>(cancellationToken, (newValue) => cancellationToken = newValue ?? cancellationToken);
            const rightsParams = await this._getIEDRightParams(frame, evaluateFormRights, refCt);
            return await this._getIEDRightsWithoutObject(ownerForm, frame, iedResult, rightsParams, requestContext);
        }

        const formEDConditions = ownerForm.usedConditionItemsId;
        const evaluatedFormEDConditions = await this.evaluateConditions(formEDConditions, false, rightType, this._getRequestContext(frame, cancellationToken));

        /*
            Pro každou z těchto úrovní je možné provést nastavení tzv. celoaplikační (= bez přiřazení role), pak toto nastavení platí obecně pro všechny role,
            nebo s vazbou na konkrétní rol,i případně kombinaci obou těchto variant. Celoaplikační nastavení bez role má při vyhodnocování podmínek přednost před nastavením s uvedenou rolí,
            pokud je např. na celoaplikační úrovni zákaz, nelze jej již pro žádnou roli povolit.

            Pořadí vyhodnocování jednotlivých úrovní je následující:
            1) Algoritmická (tj. úroveň aplikační logiky) úroveň bez role
            2) Algoritmická úroveň s rolí
            3) Interface implementační (bez role)
            4) Interface s rolí
            5) Formulář definiční
            6) Formulář implementační (bez role)
            7) Formulář s rolí

            Pro vyhodnocování podmínek na těchto úrovních platí, že restrikce z vyšší úrovně nelze zmírnit, tj. pouze směrem níže dále omezit, neboť postup vyhodnocování je ten,
            pokud je na vyšší úrovni příslušné právo False, nižší úrovně se již nevyhodnocují.
        */

        const formRightWithoutRole = ownerForm.formRights.find(r => r.roleID === ROLELESS_ROLE_ID); // TODO-rites: formRights brát takhle? asi jo; c2: ne..implicitní role může mít jiné id; c3: takhle to bude ok, implicitní !== bez role
        const formActiveRoles = ownerForm.formRights.filter(formRight => activeRoleIds.indexOf(formRight.roleID) > -1); // pouze aktivní role
        // Chceme vyhodnocovat právo editace?
        if (EnumUtils.hasFlag(rightType, RightType.edit) && !iedResult.strictlyDisabled) {
            if (this._context.canEdit && !this._context.isClientSideEncrypted) { // výsledek práva na serveru: 1) - 4); šifrovaný objekt nelze editovat
                //6) a 7)
                if (!frame.actualObject?.isNew) { // V případě isNew nechceme vyhodnocovat editační práva
                    this._evaluateEditRight(iedResult, evaluateFormRights, formActiveRoles, formRightWithoutRole, evaluatedFormEDConditions);
                } else {
                    iedResult.canEdit = true;
                }
            } else {
                iedResult.canEdit = false;
            }
        }

        // Chceme vyhodnocovat právo mazání?
        if (EnumUtils.hasFlag(rightType, RightType.delete) && !iedResult.strictlyDisabled) {
            if (this._context.canDelete) { // výsledek práva na serveru: 1) - 4)
                //6) a 7)
                this._evaluateDeleteRight(iedResult, evaluateFormRights, formActiveRoles, formRightWithoutRole, evaluatedFormEDConditions);
            } else {
                iedResult.canDelete = false;
            }
        }

        // Chceme vyhodnocovat právo vkládání?
        if (EnumUtils.hasFlag(rightType, RightType.insert) && !iedResult.strictlyDisabled) {
            const refCt = new ReferenceParameter<CancellationToken>(cancellationToken, (newValue) => cancellationToken = newValue ?? cancellationToken);
            const rightsParams = await this._getIEDRightParams(frame, evaluateFormRights, refCt);
            await this._evaluateInsertRight(iedResult, frame, rightsParams, cancellationToken);
        }

        return iedResult;
    }

    public async evaluateLevelSubsetItemRights(rightType: RightType, itemRights: ReadonlyArray<IItemRights>, cachedConditions: Map<string, Promise<IEvaluatedConditionResult>>,
        isStatic: boolean | null, frame: IFrameWithRights, ct: CancellationToken, roleIds?: string[]): Promise<Triplet<boolean | null, string[] | null, string[] | null>> {
        const conditionItemsToEvaluate: IItemRights[] = [];
        const conditionItemsToApply: IItemRights[] = [];

        const conditionsCachedResults: Promise<IEvaluatedConditionResult>[] = [];
        for (const itemRight of itemRights) {
            if (itemRight.conditionID != null) {
                //TODO: tady se v případě ME může vratít hodnota rovnou podle typu práva
                //ještě potřeba dořešit AND/OR se serverovou úrovní? Asi někde níž v téhle metodě, přeskočit uložení do keše!
                conditionItemsToApply.push(itemRight);
                const cachedValue = cachedConditions?.get(itemRight.conditionID);
                if (cachedValue) {
                    conditionsCachedResults.push(cachedValue);
                } else {
                    conditionItemsToEvaluate.push(itemRight);
                }
            }
        }

        let conditionsEvaluationResultsPromise: Promise<IEvaluatedConditionResult[]> = Promise.resolve([]);
        if (conditionItemsToEvaluate.length > 0) {
            conditionsEvaluationResultsPromise = this.evaluateConditions(conditionItemsToEvaluate.map(item => item.conditionID), isStatic, rightType, this._getRequestContext(frame, ct));
        }

        for (const item of conditionItemsToEvaluate) {
            const resultPromise = async () => {
                return (await conditionsEvaluationResultsPromise)?.find(result => result.ID === item.conditionID);
            };
            cachedConditions.set(item.conditionID, resultPromise());
        }

        let conditionsEvaluationResults = await conditionsEvaluationResultsPromise;
        if (conditionsCachedResults.length > 0) {
            conditionsEvaluationResults = conditionsEvaluationResults.concat(await Promise.all(conditionsCachedResults));
        }

        return this._combineConditionResults(rightType, itemRights, conditionsEvaluationResults, roleIds);
    }

    /**
     * Zkombinuje výsledky práv a vyhodnocených podmínek
     * @param rightType
     * @param itemRights
     * @param evaluatedConditions
     * @param multiEditActive
     * @param roleIds
     */
    protected _combineConditionResults(rightType: RightType, itemRights: ReadonlyArray<IItemRights>, evaluatedConditions: IEvaluatedConditionResult[], roleIds?: string[]): Triplet<boolean | null, string[] | null, string[] | null> {
        if (!itemRights || itemRights.length <= 0)
            return null;

        const hadInputRoleIds = roleIds != null && roleIds instanceof Array;
        let passedRoleIds: string[];
        const rejectedRoleIds: string[] = [];
        if (hadInputRoleIds) {
            passedRoleIds = [...roleIds];
        }

        const logicalOperator = this.getOperatorForRightType(rightType);
        const defaultValue: boolean = this.getDefaultRightValueForRightType(rightType);
        const initialValue: boolean = !defaultValue;
        let value: boolean = initialValue;

        for (const itemRight of itemRights) {
            let newValue: boolean = null;
            let conditionFailed = false;

            if (itemRight && itemRight.conditionID) {
                if (!evaluatedConditions)
                    newValue = false;
                else {
                    for (const evaluatedCondition of evaluatedConditions) {
                        if (evaluatedCondition.ID === itemRight.conditionID) {
                            newValue = evaluatedCondition.Result;
                            //TODO-rites: # !! pokud máme např required a přijde sem a) podmínka jejíž výsledek je true b) napevno false, mělo by být výsledkem false; tady se to ale ukončí, zkombinuje(OR) s výchozí hodnotou a vypadne nesprávný výsledek true
                            break;
                        }
                    }

                    // If condition evaluation failed, use default value, as if there is no condition.
                    if (newValue == null) {
                        newValue = defaultValue;
                        conditionFailed = true;
                    }
                }
            } else {
                if (itemRight && itemRight.value != null)
                    newValue = itemRight.value;
            }

            if (newValue != null) {
                if (this._isNegative(itemRight) && !conditionFailed) {
                    newValue = !newValue;
                }

                if (hadInputRoleIds) {
                    if (logicalOperator === LogicalOperator.or && newValue === false) {
                        passedRoleIds = ArrayUtils.filterSplit(passedRoleIds, roleId => roleId !== itemRight.roleID, rejectedRoleIds);

                        if (passedRoleIds.length <= 0) {
                            // value = !defaultValue;
                            value = false;
                            break;
                        } else {
                            newValue = true;
                        }
                    } else if (logicalOperator === LogicalOperator.and && newValue === true) {
                        passedRoleIds = ArrayUtils.filterSplit(passedRoleIds, roleId => roleId !== itemRight.roleID, rejectedRoleIds);

                        if (passedRoleIds.length <= 0) {
                            // value = !defaultValue;
                            value = true;
                            break;
                        } else {
                            newValue = false;
                        }
                    }
                }

                value = LogicalOperations.binaryOperation(logicalOperator, value, newValue);

                if (!hadInputRoleIds && ((logicalOperator === LogicalOperator.and && !value) || (logicalOperator === LogicalOperator.or && value)))
                    break;
            }
        }

        return new Triplet(value, passedRoleIds, rejectedRoleIds);
    }

    protected _isNegative(right: IItemRights): boolean {
        return right.isNegative;
    }

    public getOperatorForRightType(rightType: RightType): LogicalOperator {
        //return this._getDefaultRightValueForRightType(rightType) ? LogicalOperator.and : LogicalOperator.or;
        switch (rightType) {
            case RightType.required:
            case RightType.postponedRequired:
            case RightType.recommended:
            case RightType.editing:
            case RightType.multiEditing:
            case RightType.interactive: //TODO-rites: ?nahradit propertou "isActive"
            case RightType.useDecryption:
            case RightType.useDigitalSign:
            case RightType.useEncryption:
                return LogicalOperator.and;

            case RightType.insert:
            case RightType.edit:
            case RightType.delete:
            case RightType.visible:
            case RightType.enabled:
            case RightType.enabledVisual:
            case RightType.closeForm:
            case RightType.confirmIf:
            case RightType.openAsEnabled:
            case RightType.validateIf:
                return LogicalOperator.or;
            default:
                this._logger.warn(`Missing operator for rightype "${RightType[rightType]}"!`); // `
                return LogicalOperator.or;
        }
    }

    public getDefaultRightValueForRightType(rightType: RightType): boolean {
        switch (rightType) {
            case RightType.insert:
            case RightType.edit:
            case RightType.delete:
            case RightType.visible:
            case RightType.enabled:
            case RightType.interactive: //TODO-rites: ?nahradit propertou "isActive"
            case RightType.editing:
                return true;

            case RightType.required:
            case RightType.postponedRequired:
            case RightType.recommended:
            case RightType.closeForm:
            case RightType.confirmIf:
            case RightType.openAsEnabled:
            case RightType.useDecryption:
            case RightType.useDigitalSign:
            case RightType.useEncryption:
            case RightType.validateIf:
            case RightType.multiEditing:
                return false;

            default:
                this._logger.warn(`Missing default right value for rightype "${RightType[rightType]}"!`); // `
                return false;
        }
    }
}
